import React from 'react';
import { IconInterface, LinkInterface, TrackingInterface } from '../../types';
import {
  LinkDefault,
  LinkEmphasized,
  LinkEmphasizedImage,
  LinkHamburger,
  LinkExternal,
  LinkInternal,
  LinkDownload,
  LinkTile,
} from './renderings';

const Link = ({ variant, ...rest }: LinkProps) => {
  switch (variant) {
    case 'hamburger':
      return <LinkHamburger {...rest} />;
    case 'emphasized':
      return <LinkEmphasized {...rest} />;
    case 'emphasizedTopIcon':
      return <LinkEmphasized withTopIcon {...rest} />;
    case 'emphasizedSmall':
      return <LinkEmphasized isSmall {...rest} />;
    case 'emphasizedImage':
      return <LinkEmphasizedImage {...rest} />;
    case 'emphasizedImageNavigation':
      return <LinkEmphasizedImage isSmall {...rest} />;
    case 'emphasizedImageLarge':
      return <LinkEmphasizedImage isLarge {...rest} />;
    case 'emphasizedImageMedium':
      return <LinkEmphasizedImage isSmall halfImageMobile {...rest} />;
    case 'emphasizedImageSmall':
      return <LinkEmphasizedImage isSmall halfImage {...rest} />;
    case 'external':
      return <LinkExternal {...rest} />;
    case 'internal':
      return <LinkInternal {...rest} />;
    case 'download':
      return <LinkDownload {...rest} />;
    case 'tile':
    case 'tileIcon':
    case 'tileIconWithShadow':
      return <LinkTile {...rest} />;
    case 'tileIconLarge':
      return <LinkTile isLarge {...rest} />;
    default:
      return <LinkDefault {...rest} />;
  }
};

export type LinkVariant =
  | 'emphasized'
  | 'emphasizedTopIcon'
  | 'emphasizedSmall'
  | 'emphasizedImage'
  | 'emphasizedImageNavigation'
  | 'emphasizedImageSmall'
  | 'emphasizedImageMedium'
  | 'emphasizedImageLarge'
  | 'hamburger'
  | 'external'
  | 'internal'
  | 'download'
  | 'tile'
  | 'tileIcon'
  | 'tileIconWithShadow'
  | 'tileIconLarge';

export type LinkProps = {
  /** Text of the Link */
  linkText?: string;
  /** Link Informations of the Link */
  link?: LinkInterface;
  /** Variant Name of the Link - possible values: **emphasized** */
  variant?: LinkVariant;
  /** Color of of Link - possible values: 'primary' | 'secondary' | 'light' | any valid color hash value */
  color?: 'primary' | 'secondary' | 'light' | string;
  /** Icon Name which will be displayed at the top of the LinkText */
  iconTop?: IconInterface;
  /** Icon Name which will be displayed before the LinkText */
  iconBefore?: IconInterface | null;
  /** Icon Name which will be displayed after the LinkText */
  iconAfter?: IconInterface | null;
  /** Icon Name which will be displayed after the LinkText on hover state */
  iconHover?: IconInterface;
  /** Hide linkText and only display Icon */
  iconOnly?: boolean;
  /** set Icon size */
  iconSize?: string | number;
  /** Tracking Informations of the Link */
  tracking?: TrackingInterface;
  /** Remove all Styles - renders plain unstyled a tag */
  noStyle?: boolean;
  /** Class of the Link */
  className?: string;
  /** Inline styles of the Link */
  style?: { [key: string]: string | number };
  /** Bg of the Link */
  backgroundImage?: any;
  /** Is hamburger Link */
  hamburger?: boolean;
  /** Small version */
  isSmall?: boolean;
  /** Large variant */
  isLarge?: boolean;
  /** With icon at top position */
  withTopIcon?: boolean;
  halfImage?: boolean;
  halfImageMobile?: boolean;
  role?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  tabIndex?: number;
  /** Background color of the link */
  backgroundColor?: string;
  /** Background of the link */
  background?: string;
  /** Box-shadow of the link */
  boxShadow?: string;
  /** Icon positioned in the link */
  teaserIcon?: any;
  children?: React.ReactNode;
  /** Is a download link */
  download?: boolean;
  /** Render as other tag */
  as?: React.ElementType;
};

export default Link;
